import { objectBy } from '@debbie/common/dist/util/index.js';
import { safeParse } from '@debbie/common/dist/util/json.js';
import { getTenantId } from '@debbie/cortex/dist/tenant/selected.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { z } from 'zod/v4';
import THEME from '../theme.js';
import Search from './Icons/Search.js';
import X from './Icons/X.js';
/**
 * Building blocks shared by the searchable single-selects (update types,
 * creditors, templates, …): the trigger/panel chrome, the open/close and
 * keyboard behaviour, and the recently-used section. Each select owns its own
 * data, filters and row rendering.
 */
export const useSearchableDropdown = ({ disabled, autofocus = false, panelRef, }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [hasAutofocused, setHasAutofocused] = useState(false);
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    if (open && disabled) {
        setOpen(false);
        setSearch('');
    }
    if (autofocus && !disabled && !hasAutofocused) {
        setHasAutofocused(true);
        setOpen(true);
    }
    const dismiss = () => {
        setOpen(false);
        setSearch('');
    };
    const close = () => {
        dismiss();
        triggerRef.current?.focus();
    };
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onOutsideInteraction = (e) => {
            if (containerRef.current != null &&
                e.target instanceof Node &&
                !containerRef.current.contains(e.target) &&
                panelRef?.current?.contains(e.target) !== true) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', onOutsideInteraction);
        document.addEventListener('focusin', onOutsideInteraction);
        return () => {
            document.removeEventListener('mousedown', onOutsideInteraction);
            document.removeEventListener('focusin', onOutsideInteraction);
        };
    }, [open, panelRef]);
    const containerProps = {
        ref: containerRef,
        // An enclosing modal must not consume the same Escape.
        onKeyDown: (e) => {
            if (open && e.key === 'Escape') {
                e.preventDefault();
                close();
            }
            if (open && e.key === 'Tab') {
                // Let the browser tab from the trigger, including for portaled menus.
                close();
            }
        },
    };
    const triggerProps = {
        ref: triggerRef,
        type: 'button',
        $open: open,
        disabled,
        onClick: () => {
            if (!disabled) {
                if (open) {
                    dismiss();
                }
                else {
                    setOpen(true);
                }
            }
        },
        onKeyDown: (e) => {
            if (!open &&
                !disabled &&
                (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                e.preventDefault();
                setOpen(true);
            }
        },
    };
    return {
        open,
        close,
        search,
        setSearch,
        containerProps,
        triggerProps,
        triggerRef,
    };
};
/** Keyboard highlight over a flat list of rows, scrolled into view */
export const useHighlightedRow = (open, resetKey) => {
    const [highlight, setHighlight] = useState({ open, resetKey, index: 0 });
    const rowRefs = useRef([]);
    const changed = highlight.open !== open || highlight.resetKey !== resetKey;
    if (changed) {
        setHighlight({ open, resetKey, index: 0 });
    }
    const highlightedIndex = changed ? 0 : highlight.index;
    const setHighlightedIndex = (value) => {
        setHighlight((current) => ({
            open,
            resetKey,
            index: typeof value === 'function' ? value(current.index) : value,
        }));
    };
    useEffect(() => {
        if (open) {
            rowRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex, open]);
    const handleKeyDown = (e, optionCount, onEnter) => {
        if (optionCount === 0) {
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex((i) => (i + 1) % optionCount);
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((i) => (i - 1 + optionCount) % optionCount);
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            onEnter(Math.min(highlightedIndex, optionCount - 1));
        }
    };
    const rowProps = (index) => ({
        ref: (el) => {
            rowRefs.current[index] = el;
        },
        $highlighted: index === highlightedIndex,
        onMouseEnter: () => setHighlightedIndex(index),
    });
    return { handleKeyDown, rowProps };
};
// Recently selected ids are tenant-specific, so a user working across tenants
// keeps a list per tenant instead of one list where most entries never match
const recentsKey = (storageKey) => `${storageKey}.${getTenantId() ?? ''}`;
/** Recently selected ids, newest first, kept in localStorage per select */
export const readRecents = (storageKey) => {
    try {
        const raw = localStorage.getItem(recentsKey(storageKey));
        if (raw == null) {
            return [];
        }
        const storedIds = safeParse(raw, z.array(z.string()));
        return storedIds.ok ? storedIds.data : [];
    }
    catch {
        return [];
    }
};
export const storeRecent = (storageKey, id) => {
    try {
        const recents = [id]
            .concat(readRecents(storageKey).filter((r) => r !== id))
            .slice(0, 8);
        localStorage.setItem(recentsKey(storageKey), JSON.stringify(recents));
    }
    catch {
        // Recents are a convenience; ignore storage failures
    }
};
const RECENTS_SHOWN = 5;
/**
 * Splits the matches into a recently-used section and the rest. Recents only
 * lead the list while browsing; a search is already targeted. `rows` is the
 * flat list that drives keyboard highlighting across both sections.
 */
export const useRecentRows = (storageKey, getId, 
/** What a recent entry is matched on; a row id by default */
getRecentsKey, open, search, matches) => {
    // Read once per opening: the list is only needed while the panel shows
    const recentIds = useMemo(() => (open ? readRecents(storageKey) : []), [open, storageKey]);
    const recentRows = useMemo(() => {
        if (search.trim() !== '') {
            return NO_ROWS;
        }
        const matchesById = objectBy(matches, getRecentsKey);
        return recentIds
            .flatMap((id) => {
            const match = matchesById[id];
            return match !== undefined ? [match] : [];
        })
            .slice(0, RECENTS_SHOWN);
    }, [recentIds, search, matches, getRecentsKey]);
    const rest = useMemo(() => {
        if (recentRows.length === 0) {
            return matches;
        }
        const recentRowIds = new Set(recentRows.map(getId));
        return matches.filter((item) => !recentRowIds.has(getId(item)));
    }, [matches, recentRows, getId]);
    const rows = useMemo(() => (recentRows.length === 0 ? matches : recentRows.concat(rest)), [matches, recentRows, rest]);
    return { recentRows, rest, rows };
};
/** Shared by the selects while their panel is closed, so memos stay stable */
export const NO_ROWS = [];
/** Sorting by name without constructing a collator per comparison */
export const nameCollator = new Intl.Collator();
export const SearchIcon = () => React.createElement(Search, { size: "15px", "aria-hidden": "true" });
/**
 * Clears the selection without opening the panel. Rendered as a sibling of
 * the trigger (a button cannot contain a button) and laid over its right end;
 * the trigger reserves the space with a `TriggerClearSpace`.
 */
export const TriggerClear = (props) => (React.createElement(ClearButton, { type: "button", "aria-label": props.label, title: props.label, onClick: props.onClear },
    React.createElement(X, { size: "12px", "aria-hidden": "true" })));
export const TriggerClearSpace = styled.span.attrs({ 'aria-hidden': true }) `
  flex: none;
  width: 18px;
`;
export const ACCENT = THEME.COLOR_ACCENT;
export const Container = styled.div `
  position: relative;
  display: inline-block;
  width: 100%;
  font-size: 0.9em;
  margin-bottom: ${THEME.GRID_SPACING}px;
`;
export const Trigger = styled.button `
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1px solid ${THEME.BORDER_STRONG};
  border-radius: 3px;
  background: #ffffff;
  color: #2b2b33;
  cursor: pointer;
  outline: none;
  transition:
    border-color 0.12s ease,
    box-shadow 0.12s ease;

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.$open ? ACCENT : THEME.BORDER_STRONG)};
  }

  &:focus-visible {
    border-color: ${ACCENT};
    box-shadow: 0 0 0 2px ${THEME.ACCENT_SURFACE_STRONG};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
export const TriggerLabel = styled.span `
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Fixed line box so the trigger height is constant with or without icons */
  line-height: 18px;
  color: ${(p) => (p.$placeholder === true ? THEME.TEXT_MUTED : '#2b2b33')};
`;
export const TriggerMeta = styled.span `
  flex: none;
  font-size: 0.85em;
  color: ${THEME.TEXT_MUTED};
`;
const ClearButton = styled.button `
  position: absolute;
  top: 50%;
  right: 30px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${THEME.TEXT_MUTED};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(81, 93, 118, 0.12);
    color: #2b2b33;
    outline: none;
  }
`;
/** Row tile / trigger icon colours shared by the selects */
export const PALETTE = {
    accent: { color: ACCENT, background: THEME.ACCENT_SURFACE_STRONG },
    success: { color: THEME.SUCCESS, background: 'rgba(83, 186, 65, 0.16)' },
    warning: { color: THEME.WARNING, background: 'rgba(255, 193, 7, 0.16)' },
    danger: { color: THEME.DANGEROUS, background: 'rgba(215, 97, 92, 0.14)' },
    neutral: { color: THEME.TEXT_MUTED, background: THEME.FILL },
};
/** Coloured icon in front of the selected label */
export const TriggerIcon = styled.span `
  flex: none;
  display: inline-flex;
  color: ${(p) => p.$palette.color};

  & svg {
    width: 14px;
    height: 14px;
  }
`;
/** Coloured icon tile at the start of a row */
export const RowTile = styled.span `
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: ${(p) => p.$palette.background};
  color: ${(p) => p.$palette.color};
  font-size: 0.7em;
  font-weight: 700;
  letter-spacing: 0.04em;

  & svg {
    width: 13px;
    height: 13px;
  }
`;
export const Chevron = styled.span `
  flex: none;
  color: ${THEME.TEXT_MUTED};
  font-size: 0.8em;
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
  transition: transform 0.12s ease;
`;
export const DropPanel = styled.div `
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  min-width: 280px;
  background: #ffffff;
  border: 1px solid ${THEME.BORDER_STRONG};
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(20, 20, 40, 0.16);
  overflow: hidden;
  outline: none;
`;
export const SearchRow = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid ${THEME.BORDER};
`;
export const SearchIconWrap = styled.span `
  flex: none;
  display: inline-flex;
  color: ${THEME.TEXT_MUTED};
`;
export const SearchField = styled.input `
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  color: #2b2b33;

  &::placeholder {
    color: ${THEME.COLOR_GREY};
  }
`;
export const FilterChips = styled.div `
  display: flex;
  gap: 5px;
  padding: 8px 12px;
  border-bottom: 1px solid ${THEME.BORDER};
  background: ${THEME.SURFACE_INSET};
  overflow-x: auto;
`;
export const FilterChip = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? ACCENT : THEME.BORDER_STRONG)};
  background: ${(p) => (p.$active ? THEME.ACCENT_SURFACE : '#ffffff')};
  color: ${(p) => (p.$active ? ACCENT : THEME.TEXT_MUTED)};
  font-size: 0.82em;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  outline: none;
  transition:
    border-color 0.12s ease,
    background 0.12s ease,
    color 0.12s ease;

  &:hover {
    border-color: ${ACCENT};
    color: ${ACCENT};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${THEME.ACCENT_SURFACE_STRONG};
  }
`;
export const ChipIcon = styled.span `
  display: inline-flex;

  & svg {
    width: 11px;
    height: 11px;
  }
`;
export const ChipCount = styled.span `
  font-weight: 400;
  opacity: 0.7;
`;
export const List = styled.div `
  max-height: 340px;
  overflow-y: auto;
  padding: 4px 0;
`;
export const SectionHeader = styled.div `
  padding: 8px 14px 3px;
  font-size: 0.68em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${THEME.TEXT_SUBTLE};
  user-select: none;
`;
export const Row = styled.div `
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 14px;
  cursor: pointer;
  user-select: none;
  background: ${(p) => (p.$highlighted === true ? THEME.SURFACE_INSET : 'transparent')};

  &:hover {
    background: ${THEME.SURFACE_INSET};
  }

  ${(p) => (p.$selected === true ? `box-shadow: inset 2px 0 0 ${ACCENT};` : '')}

  &[aria-disabled='true'] {
    cursor: default;

    > span:not([aria-disabled='false']) {
      opacity: 0.5;
    }
  }
`;
export const RowText = styled.span `
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;
export const RowLabel = styled.span `
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #2b2b33;
`;
export const RowDescription = styled.span `
  font-size: 0.8em;
  line-height: 1.3;
  color: ${THEME.TEXT_MUTED};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
export const EmptyRow = styled.div `
  padding: 16px 14px;
  text-align: center;
  color: ${THEME.COLOR_GREY};
`;
//# sourceMappingURL=SearchableDropdown.js.map